import type { FC } from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import type { Company } from '@xing-com/crate-common-graphql-types';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';

import { CompanyLoggedout } from '../../components/company-loggedout';
import {
  LOGGED_IN_CARDS_PER_PAGE,
  LOGGED_OUT_CARDS_PER_PAGE,
} from '../../config';
import { usePagination } from '../../hooks/use-pagination';
import { useRecentlyClaimedCompanies } from '../../hooks/use-recently-claimed-companies';
import { useTrackPage } from '../../hooks/use-track-page';
import { scrollToTop } from '../../utils';
import {
  pagesTrackingName,
  useNwtTrackingCompanies,
} from '../../utils/tracking';

export const RecentlyClaimedCompanies: FC = () => {
  const { isLoggedIn } = useLoginState();

  const trackNwtEvent = useNwtTrackingCompanies();

  const intl = useIntl();

  useEffect(() => {
    trackNwtEvent({
      eventSchema: 'basic',
      page: 'companies/new_companies',
      event: 'viewed_screen',
    });
    scrollToTop();
  }, []);

  const limit = isLoggedIn
    ? LOGGED_IN_CARDS_PER_PAGE
    : LOGGED_OUT_CARDS_PER_PAGE;

  const { changePage, activePage } = usePagination();

  const { data, loading, error } = useRecentlyClaimedCompanies({
    limit,
    offset: limit * (activePage - 1),
  });

  useTrackPage({ pageName: pagesTrackingName.recentlyClaimed });
  const { collection, total } = data?.companiesRecentlyClaimed ?? {};

  return (
    <CompanyLoggedout
      companies={collection?.map((item) => item?.company as Company)}
      total={total}
      headerText={intl.formatMessage({
        id: 'CP_100_LATELY_BOUGHT_CPS_HEADLINE_NEW',
      })}
      authenticated={isLoggedIn}
      limit={limit}
      activePage={activePage}
      changePage={changePage}
      loading={loading}
      error={error}
      companyType="recently-claimed"
    />
  );
};
